/**
 * HTML Sanitization Utilities
 * 
 * Provides XSS protection for user-generated or external HTML content.
 * Uses DOMPurify to sanitize HTML before rendering with dangerouslySetInnerHTML.
 * 
 * SECURITY: Always use sanitizeHTML() before rendering untrusted HTML.
 */

import DOMPurify from 'dompurify';

/**
 * Sanitize HTML string to prevent XSS attacks
 * @param {string} html - Raw HTML string
 * @param {Object} config - Optional DOMPurify configuration
 * @returns {string} Sanitized HTML safe for rendering
 */
export const sanitizeHTML = (html, config = {}) => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  const defaultConfig = {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'blockquote', 'pre', 'code', 'a', 'img', 'span',
      'div', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'hr', 'sub', 'sup'
    ],
    ALLOWED_ATTR: [
      'href', 'title', 'target', 'rel', 'src', 'alt', 'class', 'id',
      'style', 'width', 'height', 'align'
    ],
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp|data):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    ...config
  };

  return DOMPurify.sanitize(html, defaultConfig);
};

/**
 * Create sanitized HTML object for React dangerouslySetInnerHTML
 * @param {string} html - Raw HTML string
 * @param {Object} config - Optional DOMPurify configuration
 * @returns {Object} Object with __html property containing sanitized HTML
 */
export const createSafeHTML = (html, config = {}) => {
  return { __html: sanitizeHTML(html, config) };
};

/**
 * Sanitize HTML with minimal restrictions (for rich text editors)
 * Allows more tags and attributes for editorial content
 */
export const sanitizeRichHTML = (html) => {
  return sanitizeHTML(html, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'blockquote', 'pre', 'code', 'a', 'img', 'span',
      'div', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'hr', 'sub', 'sup',
      'b', 'i', 's', 'strike', 'del', 'ins', 'mark', 'small', 'abbr', 'cite',
      'q', 'kbd', 'samp', 'var', 'dfn', 'address', 'time', 'figure', 'figcaption'
    ],
    ALLOWED_ATTR: [
      'href', 'title', 'target', 'rel', 'src', 'alt', 'class', 'id',
      'style', 'width', 'height', 'align', 'colspan', 'rowspan', 'type',
      'start', 'reversed', 'datetime', 'cite', 'data-*'
    ],
    ALLOW_DATA_ATTR: true
  });
};

/**
 * Create safe HTML object for rich content
 */
export const createSafeRichHTML = (html) => {
  return { __html: sanitizeRichHTML(html) };
};
